import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { featureService } from '@common/api/featureService';

interface IndustryRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const IndustryRequestModal: React.FC<IndustryRequestModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    industry: '',
    companySize: '',
    message: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Work email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.companyName.trim()) newErrors.companyName = 'Company Name is required';
    if (!formData.industry.trim()) newErrors.industry = 'Industry is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setSubmitError(null);

    try {
            // 构建发送到 Slack 的消息内容
            await featureService.sendGenericMessage({
                title: 'New Industry Request',
                content: {
                    'First Name': formData.firstName,
                    'Last Name': formData.lastName,
                    'Email': formData.email,
                    'Company Name': formData.companyName,
                    'Industry': formData.industry,
                    'Company Size': formData.companySize || 'Not specified',
                    'Message': formData.message || 'No message provided',
                }
            });

            console.log('表单提交成功', formData);
            setIsSubmitted(true);
            setErrors({});
        } catch (error) {
            console.error('表单提交失败', error);
            setSubmitError('Failed to submit your request. Please try again.');
        } finally {
            setIsLoading(false);
        }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        companyName: '',
        industry: '',
        companySize: '',
        message: ''
      });
    }, 200);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined } as any));
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
      <div 
        className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-text-sub hover:text-text-main rounded-full hover:bg-gray-100 transition-colors z-10"
        >
          <X size={20} />
        </button>

        <div className="overflow-y-auto custom-scrollbar">
          <div className="p-8 sm:p-10">
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center text-center py-10 animate-in fade-in duration-300">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 shadow-sm">
                  <Check size={40} className="text-green-600" strokeWidth={3} />
                </div>
                
                <h2 className="text-3xl font-bold text-text-main mb-4">Thank you!</h2>
                <p className="text-xl text-text-main font-medium mb-4">
                  We've received your request.
                </p>
                <p className="text-text-sub max-w-md mx-auto mb-10 leading-relaxed text-lg">
                  A member of our team will reach out to you within 24 hours.
                </p>
                <button
                  onClick={handleClose}
                  className="px-8 py-3 bg-text-main text-white font-bold rounded-xl hover:bg-text-main/90 transition-colors shadow-lg"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="text-center mb-10">
                  <h2 className="text-3xl font-bold text-text-main mb-3">Let's talk about your business</h2>
                  <p className="text-text-sub text-lg">
                    Tell us a bit about yourself and we'll be in touch shortly.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-text-main">First Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={clsx(
                          "w-full px-4 py-3 rounded-xl border bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20",
                          errors.firstName ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-[#1A162F]"
                        )}
                        placeholder="First name"
                      />
                      {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-text-main">Last Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={clsx(
                          "w-full px-4 py-3 rounded-xl border bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20",
                          errors.lastName ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-[#1A162F]"
                        )}
                        placeholder="Last name"
                      />
                      {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-text-main">Work Email <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={clsx(
                        "w-full px-4 py-3 rounded-xl border bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20",
                        errors.email ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-[#1A162F]"
                      )}
                      placeholder="you@company.com"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-text-main">Company Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className={clsx(
                        "w-full px-4 py-3 rounded-xl border bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20",
                        errors.companyName ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-[#1A162F]"
                      )}
                      placeholder="Your company name"
                    />
                    {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-text-main">Industry <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className={clsx(
                        "w-full px-4 py-3 rounded-xl border bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20",
                        errors.industry ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-[#1A162F]"
                      )}
                      placeholder="e.g., Healthcare, Logistics, Real Estate"
                    />
                    {errors.industry && <p className="text-red-500 text-xs mt-1">{errors.industry}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-text-main">Company Size</label>
                    <select
                      name="companySize"
                      value={formData.companySize}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20 appearance-none"
                    >
                      <option value="">Select company size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="500+">500+ employees</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-text-main">Message (optional)</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#1A162F] transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20 resize-none"
                      placeholder="Tell us about your specific needs..."
                    />
                  </div>

                  <div className="pt-4">
                    {submitError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                        <p className="text-red-600 text-sm text-center">{submitError}</p>
                    </div>
                    )}

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-4 bg-text-main text-white font-bold rounded-xl hover:bg-text-main/90 transition-colors shadow-lg flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 size={20} className="animate-spin" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <span>Submit Request</span>
                      )}
                    </button>
                    <p className="text-center text-xs text-text-sub mt-4">
                      By submitting, you agree to our Privacy Policy.
                    </p>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default IndustryRequestModal;
